/**
 * 평가사 상세 프로필 컴포넌트
 * InspectorManage.js에서 분리
 *
 * 2026-10-29 — 화면을 v2 키트(히어로·카드·장부)로 평가 화면 다른 탭과 맞춤. 수정 폼 필드·저장 로직은 그대로.
 */
import React, { useState } from 'react';
import { useIsMobile } from '../utils/useMobile';
import { T, S, FONT, NUM, R } from '../v2/theme';
import { HeroStrip, Card, Ledger, EmptyState, Pill } from '../v2/kit';
import { LevelBadge, Stars } from './CommonUI';
import PageBar from '../v2/eval/parts/PageBar';

// ─── 등급 기준 (색은 CommonUI.LevelBadge) ───
var LEVELS = {
  '마스터': { desc: '100건+ & 평점 4.5+', benefit: '검차비 +20%, 우선 콜' },
  '시니어': { desc: '50건+ & 평점 4.0+', benefit: '검차비 +10%' },
  '일반':   { desc: '기본', benefit: '기본 검차비' },
};

var sub = { fontSize: FONT.label, color: T.faint };
var right = { display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' };
var row = { display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid ' + T.bdSoft, fontSize: FONT.body, minWidth: 0 };
var rowLabel = { width: 72, flexShrink: 0, color: T.muted };
var cardGap = { marginBottom: 14 };
var inputFull = Object.assign({}, S.input, { width: '100%', boxSizing: 'border-box' });
var mapBtn = Object.assign({}, S.btn, S.btnSm, { textDecoration: 'none', display: 'inline-flex', alignItems: 'center' });

// 시간 경과 표시
var getTimeAgo = function(dateStr) {
  if (!dateStr) return '정보 없음';
  var diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return '방금 전';
  if (diff < 3600) return Math.floor(diff / 60) + '분 전';
  if (diff < 86400) return Math.floor(diff / 3600) + '시간 전';
  return Math.floor(diff / 86400) + '일 전';
};

// ─── 평가사 상세 프로필 ───
function InspectorDetail(props) {
  var inspector = props.inspector;
  var onBack = props.onBack;
  var onUpdate = props.onUpdate;

  var avg = inspector.avg_rating;
  var reviews = inspector.reviews || [];
  var scores = inspector.avg_scores || {};
  var distrib = inspector.distribution || [];
  var [editing, setEditing] = useState(false);
  // Wave S5(a) (2026-05-21) — 송금 계좌 추가 (정산서 PDF 표기용)
  var [editForm, setEditForm] = useState({ name: inspector.name || '', phone: inspector.phone || '', area: inspector.area || '', pin_code: inspector.pin_code || '', status: inspector.status || 'active', telegram_chat_id: inspector.telegram_chat_id || '', bank_name: inspector.bank_name || '', account_number: inspector.account_number || '' });
  var isMobile = useIsMobile();
  var maxCount = Math.max.apply(null, distrib.map(function(d) { return d.count; }).concat([1]));
  var barColor = function(val) { return val >= 4.5 ? T.ok : val >= 3.5 ? T.dotWarn : T.danger; };
  var setF = function(key, val) { setEditForm(Object.assign({}, editForm, (function() { var o = {}; o[key] = val; return o; })())); };
  var field = function(label, node, wide) {
    return (<label style={{ display: 'block', minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}><span style={S.label}>{label}</span>{node}</label>);
  };

  // 등급까지 남은 수치 계산
  var nextLevel = inspector.level === '일반' ? '시니어' : inspector.level === '시니어' ? '마스터' : null;
  var nextReq = nextLevel === '시니어' ? { jobs: 50, rating: 4.0 } : nextLevel === '마스터' ? { jobs: 100, rating: 4.5 } : null;
  var okColor = function(ok) { return ok ? T.ok : T.danger; };

  return (
    <div>
      <PageBar back={{ label: '‹ 평가사 목록', onClick: onBack }}
        title={<React.Fragment>{inspector.name} <LevelBadge level={inspector.level || '일반'}/>
          {inspector.status === 'active' ? <Pill tone="ok">활동중</Pill> : inspector.status === 'pending' ? <Pill tone="warn">승인대기</Pill> : <Pill tone="gray">비활성</Pill>}</React.Fragment>}
        right={onUpdate ? <button type="button" style={Object.assign({}, editing ? S.btnGhost : S.btn, S.btnSm)} onClick={function() { setEditing(!editing); }}>{editing ? '수정 닫기' : '수정'}</button> : null}/>
      <HeroStrip isMobile={isMobile} metrics={[
        { key: 'rating', label: '평균 평점', value: avg, unit: '★' },
        { key: 'jobs', label: '총 검차', value: inspector.total_jobs || 0, unit: '건' },
        { key: 'recent', label: '최근 30일', value: inspector.recent_jobs || 0, unit: '건' },
        { key: 'reviews', label: '리뷰', value: reviews.length, unit: '건' },
      ]}/>

      {/* 프로필 */}
      <Card style={cardGap} pad={16} title="프로필">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', columnGap: 24 }}>
          <div style={row}><span style={rowLabel}>연락처</span><span style={NUM}>{inspector.phone || '-'}</span></div>
          <div style={row}><span style={rowLabel}>활동 지역</span><span>{inspector.area || '-'}</span></div>
          <div style={row}><span style={rowLabel}>가입</span><span style={NUM}>{inspector.join_date || inspector.created_at?.slice(0, 10) || '-'}</span></div>
          <div style={row}><span style={rowLabel}>송금 계좌</span><span>{inspector.bank_name || inspector.account_number ? (inspector.bank_name || '') + ' ' + (inspector.account_number || '') : '-'}</span></div>
        </div>

        {/* 수정 폼 — Wave S5(a) (2026-05-21) 송금 계좌 포함 (정산서 PDF 표기) */}
        {editing && (
          <div style={{ marginTop: 14, padding: 14, background: T.head, border: '1px solid ' + T.bd, borderRadius: R.card }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
              {field('이름', <input value={editForm.name} onChange={function(e) { setF('name', e.target.value); }} style={inputFull} />)}
              {field('전화번호', <input value={editForm.phone} onChange={function(e) { setF('phone', e.target.value.replace(/[^0-9]/g, '')); }} style={inputFull} />)}
              {field('활동지역', <input value={editForm.area} onChange={function(e) { setF('area', e.target.value); }} style={inputFull} />)}
              {field('PIN', <input value={editForm.pin_code} onChange={function(e) { setF('pin_code', e.target.value); }} maxLength={4} style={inputFull} />)}
              {field('은행', <input value={editForm.bank_name} onChange={function(e) { setEditForm(Object.assign({}, editForm, { bank_name: e.target.value })); }} placeholder="신한은행" style={inputFull} />)}
              {field('계좌번호', <input value={editForm.account_number} onChange={function(e) { setEditForm(Object.assign({}, editForm, { account_number: e.target.value })); }} placeholder="110-123-456789" style={inputFull} />)}
              {field('텔레그램 Chat ID', <input value={editForm.telegram_chat_id} onChange={function(e) { setF('telegram_chat_id', e.target.value); }} placeholder="봇에 /start 후 받은 숫자" style={inputFull} />, true)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: FONT.body, cursor: 'pointer' }}>
                <input type="checkbox" checked={editForm.status === 'active'} onChange={function(e) { setF('status', e.target.checked ? 'active' : 'inactive'); }} />
                활성 상태
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={function() { setEditing(false); }} style={S.btn}>취소</button>
                <button type="button" onClick={function() { onUpdate(inspector.id, editForm); setEditing(false); }} style={S.btnPrimary}>저장</button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* 현재 위치 */}
      {inspector.current_lat && inspector.current_lng && (
        <Card style={cardGap} title="현재 위치" count={getTimeAgo(inspector.location_updated_at)} right={<span style={right}>
          <a href={'https://map.naver.com/v5/?c=' + inspector.current_lng + ',' + inspector.current_lat + ',15,0,0,0,dh'} target="_blank" rel="noopener noreferrer" style={mapBtn}>네이버지도</a>
          <a href={'https://map.kakao.com/?urlX=' + inspector.current_lng + '&urlY=' + inspector.current_lat + '&urlLevel=3&map_type=TYPE_MAP'} target="_blank" rel="noopener noreferrer" style={mapBtn}>카카오맵</a>
        </span>}/>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 14, marginBottom: 14 }}>
        {/* 항목별 평점 */}
        <Card title="항목별 평점" pad={16}>
          {[
            ['신속성', scores.speed, '일정 준수, 콜 수락 후 검차까지 속도'],
            ['정확성', scores.accuracy, '도막 측정, 사고이력 판단 정확도'],
            ['꼼꼼함', scores.detail, '사진 수량/품질, 메모 상세도'],
          ].map(function(item) {
            var val = item[1] || 0;
            return (<div key={item[0]} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <span style={{ fontSize: FONT.body, fontWeight: 700, color: T.ink }}>{item[0]}</span>
                <b style={Object.assign({ color: barColor(val) }, NUM)}>{val}</b>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: T.bdSoft }}>
                <div style={{ height: 6, borderRadius: 3, background: barColor(val), width: ((val / 5) * 100) + '%' }} />
              </div>
              <div style={Object.assign({ marginTop: 3 }, sub)}>{item[2]}</div>
            </div>);
          })}
        </Card>

        {/* 평점 분포 */}
        <Card title="평점 분포" pad={16}>
          {!distrib.length ? <span className="mute">리뷰가 아직 없습니다</span> : distrib.map(function(d) {
            return (<div key={d.score} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={Object.assign({ fontSize: FONT.body, fontWeight: 700, width: 24, textAlign: 'right' }, NUM)}>{d.score}★</span>
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: T.bdSoft }}>
                <div style={{ height: 8, borderRadius: 4, background: T.dotWarn, width: ((d.count / maxCount) * 100) + '%' }} />
              </div>
              <span style={Object.assign({ width: 32, textAlign: 'right' }, sub, NUM)}>{d.count}건</span>
            </div>);
          })}
        </Card>

        {/* 등급 진행 */}
        <Card title="등급" pad={16}>
          {Object.entries(LEVELS).map(function(entry) {
            var lv = entry[0];
            var cfg = entry[1];
            var isCurrent = inspector.level === lv;
            return (<div key={lv} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: R.md, marginBottom: 4,
              background: isCurrent ? T.accentTint : 'transparent', border: '1px solid ' + (isCurrent ? T.accentBd : 'transparent') }}>
              <LevelBadge level={lv}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: FONT.body, fontWeight: 600, color: isCurrent ? T.ink : T.muted }}>{cfg.desc}</div>
                <div style={sub}>{cfg.benefit}</div>
              </div>
              {isCurrent && <Pill tone="blue">현재</Pill>}
            </div>);
          })}
          {/* 다음 등급까지 */}
          {nextLevel && nextReq && (
            <div style={{ marginTop: 8, fontSize: FONT.body, color: T.muted, lineHeight: 1.6 }}>
              <b style={{ color: T.ink }}>{nextLevel}</b>까지: 검차 <b style={{ color: okColor(inspector.total_jobs >= nextReq.jobs) }}>
                {inspector.total_jobs}/{nextReq.jobs}건{inspector.total_jobs >= nextReq.jobs ? ' ✓' : ' (' + (nextReq.jobs - inspector.total_jobs) + '건 남음)'}
              </b>, 평점 <b style={{ color: okColor(avg >= nextReq.rating) }}>
                {avg}/{nextReq.rating}{avg >= nextReq.rating ? ' ✓' : ' (+' + Number(nextReq.rating - avg).toFixed(1) + ' 필요)'}
              </b>
            </div>
          )}
        </Card>
      </div>

      {/* 최근 리뷰 */}
      <Card title="최근 리뷰" count={reviews.length}>
        {!reviews.length ? <EmptyState loaded text="받은 리뷰가 없습니다"/> : (
          <Ledger minWidth={760} foot={<span className="mute">업체가 검차 후 남긴 평가</span>}>
            <thead><tr>
              <th style={{ width: 110 }}>날짜</th><th style={{ width: 110 }}>평점</th><th style={{ width: 220 }}>평가 · 차량</th><th>코멘트</th><th style={{ width: 170 }}>항목</th>
            </tr></thead>
            <tbody>
              {reviews.map(function(r) {
                // 서버 행 = inspector_reviews.* + order_no·car_model (score_overall/score_speed…/created_at).
                //   옛 화면이 score/speed/date/car 를 읽어 날짜·별점·항목이 늘 비어 있던 것을 바로잡음.
                return (<tr key={r.id}>
                  <td style={NUM}>{String(r.created_at || r.date || '').slice(0, 10) || '-'}</td>
                  <td><Stars value={r.score_overall != null ? r.score_overall : r.score} size={12}/></td>
                  <td><span style={NUM}>{r.order_no}</span> <span style={sub}>{r.car_model || r.car}</span></td>
                  <td style={{ whiteSpace: 'normal' }}>{r.comment || <span className="mute">-</span>}</td>
                  <td style={sub}>신속 {r.score_speed != null ? r.score_speed : r.speed} · 정확 {r.score_accuracy != null ? r.score_accuracy : r.accuracy} · 꼼꼼 {r.score_detail != null ? r.score_detail : r.detail}</td>
                </tr>);
              })}
            </tbody>
          </Ledger>
        )}
      </Card>
    </div>
  );
}

export default InspectorDetail;
